import { motion, MotionProps } from "framer-motion";
import { AnchorHTMLAttributes, forwardRef } from "react";
import {
  composeRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { useLinkClickHandler } from "../../navigation";
import { cn } from "../../cn";
import Icon from "../../foundations/Icons/Icon";
import { IconType } from "../../foundations/Icons/types";
import * as MenuStyles from "../Menu/styles";

const MotionButton = motion(RACButton);

function ButtonLink({
  href,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const handleClick = useLinkClickHandler(href, onClick);
  return <a href={href} onClick={handleClick} {...props} />;
}

type ButtonProps = RACButtonProps &
  MotionProps & {
    /** Icon to display on the left side of the button */
    leftIcon?: IconType;
    href?: string;
    /** Icon to display on the right side of the button */
    rightIcon?: IconType;
    children?: React.ReactNode;
    /** Controls if the icon color should change with the color font */
    iconDynamicColor?: boolean;
    /** Controls the size of the button */
    size?: "xs" | "s" | "m" | "l" | "xl";
    /** Controls the color of the button */
    color?:
      | "gradient"
      | "tangerine"
      | "grey"
      | "dark-grey"
      | "black"
      | "red"
      | "green"
      | "blue";
    /** Controls the variant of the button */
    variant?: "menu" | "default" | "square";
  };

const button = tv({
  slots: {
    base: "",
    text: "",
    icon: "size-[14px]",
  },
  variants: {
    variant: {
      menu: {
        base: MenuStyles.base,
      },
      default: {
        base: "group font-medium outline-hidden leading-6 rounded-8 transition-colors flex items-center justify-center text-black",
      },
      square: {
        base: "group font-medium outline-hidden leading-6 rounded-2 transition-colors flex items-center justify-center text-black",
      },
    },
    size: {
      xs: "text-sm h-6 p-4",
      s: "text-sm h-10 px-7",
      m: "text-sm h-12 px-7",
      l: "text-base h-14 px-8",
      xl: "text-base h-16 px-11",
    },
    color: {
      gradient: {
        base: "scale-100 border-2 border-peach text-white hover:scale-105 disabled:scale-100 transition-transform disabled:text-nude",
        icon: "bg-white group-disabled:bg-nude",
        text: "drop-shadow-sm",
      },
      tangerine: {},
      grey: {
        base: "bg-grey-2 hover:bg-grey-3 active:bg-grey-3 active:border-grey-5 focus:bg-grey-3 focus:border-grey-5 disabled:bg-grey-2 disabled:text-grey-6",
        icon: "bg-black group-disabled:bg-grey-6",
      },
      "dark-grey": {
        base: "text-white bg-grey-9 hover:bg-grey-10 active:bg-grey-10 active:border-black active:border-2 focus:border-black focus:bg-grey-10 focus:border-2 disabled:bg-black disabled:text-grey-7 border-1 border-white",
        icon: "bg-white group-disabled:bg-grey-7",
      },
      black: {},
      red: {},
      green: {},
      blue: {},
    },
  },
  compoundVariants: [
    {
      variant: "default",
      color: "tangerine",
      className: {
        base: "bg-peach hover:bg-tangerine active:bg-peach active:border-tangerine  focus:border-tangerine disabled:bg-nude disabled:text-grey-8",
        icon: "bg-black group-disabled:bg-grey-8",
      },
    },
    {
      variant: "default",
      color: "green",
      className: {
        base: "bg-green-secondary hover:bg-green active:bg-green active:border-green focus:border-green disabled:bg-nude disabled:text-grey-8",
        icon: "bg-black group-disabled:bg-grey-8",
      },
    },
    {
      variant: "default",
      color: "blue",
      className: {
        base: "bg-blue hover:bg-blue-2 active:bg-blue active:border-blue focus:border-blue disabled:bg-nude disabled:text-grey-8",
        icon: "bg-black group-disabled:bg-grey-8",
      },
    },
    {
      variant: "default",
      color: "black",
      className: {
        base: "text-white bg-black hover:bg-grey-10 active:bg-grey-10 active:border-black active:border-2 focus:border-black focus:bg-grey-10 focus:border-2 disabled:bg-black disabled:text-grey-7 border-1 border-white",
        icon: "bg-white group-disabled:bg-grey-7",
      },
    },
    {
      variant: "default",
      color: "red",
      className: {
        base: "text-white bg-red disabled:text-grey-9",
        icon: "bg-white group-disabled:bg-grey-9",
      },
    },
  ],
  defaultVariants: {
    size: "s",
    color: "tangerine",
    variant: "default",
  },
});

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = "s",
      color = "tangerine",
      children,
      leftIcon,
      rightIcon,
      variant = "default",
      iconDynamicColor = true,
      href,
      ...props
    },
    ref,
  ) => {
    const { base, icon, text } = button({ variant });
    const iconClassName = icon({ size, color });
    const textClassName = text({ size, color });

    const content = (
      <>
        {leftIcon && (
          <Icon
            className={cn(iconClassName, "mr-2")}
            size="sm"
            icon={leftIcon}
            dynamicColor={iconDynamicColor}
          />
        )}
        <span className={cn(textClassName, "flex items-center")}>
          {children}
        </span>
        {rightIcon && (
          <Icon
            className={cn(iconClassName, "ml-2")}
            size="sm"
            icon={rightIcon}
            dynamicColor={iconDynamicColor}
          />
        )}
      </>
    );

    if (href) {
      return (
        // @ts-expect-error -- Button's aria/motion props do not line up with an anchor's
        <ButtonLink
          href={href}
          {...props}
          className={base({
            size,
            color,
            className: props.className as string,
          })}
        >
          {content}
        </ButtonLink>
      );
    }

    let gradientProps = {};
    if (color === "gradient") {
      gradientProps = {
        initial: { backgroundPosition: "0 0" },
        animate: { backgroundPositionX: ["0px", "600px"] },
        transition: {
          duration: 3,
          delay: Math.random() * 2,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        },
        style: {
          backgroundImage: `linear-gradient(to right, #FFB861, #FF8670, #FF7FAF, #FF8670, #FFB861)`,
          backgroundSize: "600px 100%",
          ...props.style,
        },
      };
    }

    return (
      <MotionButton
        ref={ref}
        {...props}
        {...gradientProps}
        className={composeRenderProps(
          props.className,
          (className, renderProps) =>
            base({
              ...renderProps,
              size,
              color,
              className,
            }),
        )}
      >
        {content}
      </MotionButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
